/**
 * Shared domain + API types for the TK Call Coach SPA.
 *
 * These mirror the public Postgres schema (sql/001_init.sql) and the server API
 * contract (server/routes). They are the single source of truth the UI codes
 * against. Keep field names aligned with the DB columns and the API responses.
 *
 * IMPORTANT (the self-score gate): a staff user CANNOT receive ai_scores /
 * ai_score_items / ai-source annotations / manager final-score / flags for
 * their OWN call until they SUBMIT their self_score. This is enforced in DB RLS
 * (can_reveal_ai) and again at the server. The client must treat AI fields as
 * possibly-absent and render a locked placeholder — never assume they exist.
 */

// ── Enums (match Postgres enum types) ───────────────────────────────────────
export type AppRole = "staff" | "manager" | "admin";
export type CallDirection = "Inbound" | "Outbound";

// ── Call-type classification framework ───────────────────────────────────────
/** The 6 call_type states (5 real + the `unknown` fallback). */
export type CallType =
  | "automotive_tint"
  | "ppf"
  | "arch_residential"
  | "arch_commercial"
  | "non_sales_admin"
  | "unknown";
/** The 5 real types a manager may assign in an override. */
export const REAL_CALL_TYPES: CallType[] = [
  "automotive_tint",
  "ppf",
  "arch_residential",
  "arch_commercial",
  "non_sales_admin",
];
/** Structured call outcome (ai_scores.call_outcome, denormalized to calls). */
export type CallOutcome =
  | "booked"
  | "consult_booked"
  | "quote_given_no_commit"
  | "no_next_step"
  | "info_only"
  | "excluded";
export type CallTypeSource = "classifier" | "manual";
// ── Transcript provenance (Phase 4 live-transcript cutover) ──────────────────
/** Where the canonical transcript that was SCORED came from. */
export type TranscriptSource = "live" | "recording";
/** Where the playback audio of record came from. */
export type AudioSource = "ringcentral_recording" | "live_dual_wav" | "both" | "none";
export type PipelineStatus =
  | "discovered"
  | "recording_ready"
  | "transcribing"
  | "transcribed"
  | "scoring"
  | "scored"
  | "error"
  | "skipped";
export type SpeakerRole = "rep" | "customer" | "unknown";
export type AnnotationSource = "self" | "ai" | "manager";
export type FlagType = "exemplary" | "needs_work";
/**
 * Coaching-flag lifecycle (sql/021 + server lib/coachingLifecycle.ts):
 *   needs_review → assigned → acknowledged → follow_up → improved → closed
 * Managers move flags FORWARD along the chain (skips allowed) plus the
 * closed → needs_review reopen; the assigned REP gets exactly one transition
 * (assigned → acknowledged, reveal-gated). Runtime helpers (labels, tones,
 * legal targets) live in lib/coachingLifecycle.ts.
 */
export type FlagLifecycleStatus =
  | "needs_review"
  | "assigned"
  | "acknowledged"
  | "follow_up"
  | "improved"
  | "closed";
export type FeedbackRating = "up" | "down";
export type Confidence = "high" | "medium" | "low";

// ── Identity ────────────────────────────────────────────────────────────────
export interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  role: AppRole;
  active: boolean;
  account_ref: string;
  can_view_live_monitor: boolean;
}

export interface UserExtension {
  user_id: string;
  extension_number: string;
  account_ref: string;
}

// ── Calls ───────────────────────────────────────────────────────────────────
/** Row shape returned by GET /api/calls (list view). */
export interface Call {
  id: string;
  account_ref: string;
  direction: CallDirection;
  from_number: string | null;
  from_name: string | null;
  to_number: string | null;
  to_name: string | null;
  extension_number: string;
  ext_owner: string | null;
  started_at: string; // ISO timestamptz
  duration_sec: number;
  call_result: string | null;
  status: PipelineStatus;
  excluded_from_stats: boolean;
  rubric_version: number | null;
  // Lightweight rollups the list endpoint may join in for badges/filters.
  self_score_submitted: boolean;
  self_score_total: number | null;
  ai_score_total: number | null; // null unless can_reveal_ai for the viewer
  flag_type: FlagType | null;
  // Recording presence + review flag (both list and detail selects return these).
  recording_rc_id: string | null; // non-null = a recording exists in RingCentral
  review_requested: boolean; // user flagged this call for AI transcription+analysis
}

/**
 * The ACTUAL row shape returned by GET /api/calls (the dashboard list).
 *
 * This is the authoritative wire contract (snake_case, matches the server
 * handler in routes/calls.ts) — it carries the base call columns PLUS four
 * status-column fields the dashboard needs. Those four respect the self-score
 * gate exactly: for a STAFF caller who has NOT submitted a self-score for a
 * given call, `ai_review_score` comes back null and `manager_notes_count` comes
 * back 0; managers/admins always receive the real values.
 *
 * (The legacy `Call` interface above carries some never-populated fields —
 * `ai_score_total`, `self_score_submitted`, `flag_type` — and is kept only for
 * the CallDetail base; the LIST endpoint returns CallRow.)
 */
export interface CallRow {
  id: string;
  account_ref: string;
  direction: CallDirection;
  from_number: string | null;
  from_name: string | null;
  to_number: string | null;
  to_name: string | null;
  extension_number: string;
  ext_owner: string | null;
  /** Who actually handled the call (§7.11: override ?? detected ?? ext_owner).
   *  When this isn't the staff viewer, the row is a covered call on their line
   *  — visible but fully gated (no AI, no manager notes, no self-score). */
  evaluated_rep_id: string | null;
  /** Display name for evaluated_rep_id ("Covered by Morgan" attribution). */
  evaluated_rep_name: string | null;
  started_at: string; // ISO timestamptz
  duration_sec: number;
  call_result: string | null;
  status: PipelineStatus;
  excluded_from_stats: boolean;
  rubric_version: number | null;
  review_requested: boolean;
  recording_rc_id: string | null;
  // ── Dashboard status fields (gated for un-self-scored staff) ──────────────
  /** Submitted self-score total for the call. null if none submitted. */
  self_score: number | null;
  /** AI total. GATED: null for a staff caller who hasn't self-scored THIS call;
   *  also null when the call simply isn't AI-scored yet. */
  ai_review_score: number | null;
  /** Count of the rep's own ('self') annotations on the call (not gated). */
  user_notes_count: number;
  /** Count of manager annotations. GATED: 0 for un-self-scored staff. */
  manager_notes_count: number;
}

/**
 * GET /api/calls/stats — role-aware dashboard KPI aggregates over the caller's
 * VISIBLE set (same scoping + filters as GET /api/calls). All AI-derived numbers
 * honor the self-score reveal gate (see callsStats.ts). The `*_delta` fields are
 * present ONLY when BOTH `from` and `to` are supplied AND `to > from` (a prior
 * equal-length window exists to compare against) — otherwise they are omitted.
 */
export interface CallStats {
  total_calls: number;
  scored_calls: number;
  /** Headline = the SALES average only (never blended with service, D2). */
  avg_score: number | null;
  /** Human note: "Based on N sales calls; M service and K excluded not counted." */
  avg_score_note?: string;
  // Segmented (never blended) — §7.7.
  avg_score_sales?: number | null;
  sales_count?: number;
  avg_score_admin?: number | null;
  admin_count?: number;
  per_type?: Record<string, { avg: number | null; count: number }>;
  excluded_count?: number;
  held_out_count?: number;
  ai_reviewed_count: number;
  needs_attention_count: number;
  scored_pct: number;
  ai_reviewed_pct: number;
  needs_attention_pct: number;
  // Present only for a closed date range (both ends + positive span).
  total_calls_delta?: number;
  scored_calls_delta?: number;
  avg_score_delta?: number | null;
  avg_score_admin_delta?: number | null;
  ai_reviewed_count_delta?: number;
  needs_attention_count_delta?: number;
}

/** GET /api/calls/:id — the call plus its scoring sub-resources. */
export interface CallDetail extends Call {
  rc_call_id: string;
  rc_session_id: string | null;
  recording_storage_path: string | null;
  recording_mime: string | null;
  rep_speaker_raw: string | null;
  self_score: SelfScore | null;
  // AI fields are gated: present only when can_reveal_ai for the viewer.
  ai_reveal: AiReveal | null;
  manager_flags: ManagerFlag[];
  // ── Call-type framework UNGATED fields (§7.6) — present even before self-score ──
  call_type: CallType | null;
  call_type_source: CallTypeSource | null;
  call_type_confidence: Confidence | null;
  is_sales_opportunity: boolean | null;
  call_outcome: CallOutcome | null;
  excluded_reason: string | null;
  // Staff attribution: who actually handled it (evaluated rep) + line owner, so
  // the header can show "Kaiden · ext 1003" and flag an ext-owner mismatch (D14).
  evaluated_rep_id: string | null;
  evaluated_rep_name: string | null;
  detected_rep_name: string | null;
  detected_rep_source: string | null;
  staff_name_ext_mismatch: boolean | null;
  rep_override_id: string | null;
  ext_owner_name: string | null;
  // ── Transcript provenance (Phase 4 cutover) — UNGATED; present on the call row ──
  // Which transcript the AI score was produced from ('live' = supervised, roles
  // known; 'recording' = RingCentral recording + batch STT, roles diarized). null
  // until a transcript is resolved (e.g. not yet transcribed). `*_reason` is a
  // human-readable why (e.g. "live complete: both legs", "fallback: no live match").
  transcript_source: TranscriptSource | null;
  transcript_source_reason: string | null;
  transcript_confidence: Confidence | null;
  audio_source: AudioSource | null;
  /** recording-t0 → live-t0 offset (ms) for syncing the live transcript to the RC
   *  recording during playback. null when audio is the live dual-channel WAV. */
  live_audio_offset_ms: number | null;
}

// ── Transcript ──────────────────────────────────────────────────────────────
export interface TranscriptSegment {
  id: number; // transcript_segments.id (bigint)
  idx: number;
  speaker: SpeakerRole;
  start_ms: number;
  end_ms: number;
  text: string;
}

// ── Rubric ──────────────────────────────────────────────────────────────────
export interface RubricCriterion {
  key: string;
  label: string;
  weight: number; // points out of 100
  sort: number;
  // §4 scoring anchors (shown on the self-score form so calibration measures
  // skill, not "what does this criterion mean?"). Optional for back-compat.
  full_credit?: string | null;
  partial_credit?: string | null;
  zero_credit?: string | null;
}

// ── Self score (staff) ──────────────────────────────────────────────────────
export interface SelfScoreItem {
  criterion_key: string;
  points: number;
  note: string | null;
}

export interface SelfScore {
  total: number | null;
  started_at?: string | null;
  submitted_at: string | null; // null = draft; set on submit (locks)
  items: SelfScoreItem[];
  // The PER-TYPE rubric (with §4 anchors) the self-score form renders against,
  // returned by GET /api/calls/:id/self-score so the UI needs no separate fetch.
  rubric: RubricCriterion[];
  // The call's CURRENT type + whether classification is still pending (UI shows a
  // brief "detecting call type…" note and the tint fallback rubric, §7.4).
  call_type?: CallType | null;
  rubric_pending?: boolean;
  // The type THIS self-score was RECORDED under (§7.8). Differs from call_type
  // when a manager re-classified the call after submission → suppress the compare.
  self_score_call_type?: CallType | null;
}

/** Body for POST /api/calls/:id/self-score. */
export interface SelfScoreSubmission {
  items: Array<{ criterion_key: string; points: number; note?: string }>;
  // Fraction (0–1) of the call the rep listened to before grading. Optional.
  listen_coverage?: number;
}

// ── AI score (revealed only after self-score submit) ────────────────────────
export interface AiScoreItem {
  criterion_key: string;
  points: number;
  rationale: string | null;
  coaching_tip: string | null;
  evidence_segment_ids: number[];
  evidence_ms: number[];
  confidence: Confidence | null;
}

export interface AiScore {
  total: number;
  summary: string | null;
  scoring_confidence: Confidence | null;
  ai_audit_id: string | null; // ties feedback widget to the audit row
}

export interface Annotation {
  id: string;
  source: AnnotationSource;
  start_ms: number | null;
  segment_id: number | null;
  criterion_key: string | null;
  type: string | null;
  title: string | null;
  text: string;
}

/**
 * Timestamped feedback note keyed to the audio.
 *
 * Row shape returned by GET /api/calls/:id/annotations and POST .../annotations.
 * `start_ms` is null for a whole-call note. `source` is "self" (the rep) or
 * "manager"; AI-source annotations come back through AiReveal, not here.
 */
export interface CallNote {
  id: string;
  source: "self" | "manager";
  author_name: string | null;
  start_ms: number | null;
  text: string;
  created_at: string;
}

/** Per-criterion gap between self and AI (positive = self over-rated). */
export interface ScoreGapItem {
  criterion_key: string;
  self_points: number;
  ai_points: number;
  delta: number;
}

export interface ScoreGap {
  self_total: number;
  ai_total: number;
  delta: number;
  items: ScoreGapItem[];
}

/**
 * GET /api/calls/:id/ai-reveal (403 if !can_reveal_ai).
 *
 * Server contract (Phase 4 scoring). `score` is null when the call has not yet
 * been scored (flagged but pipeline not finished); the UI must render a pending
 * placeholder in that case rather than assume a score exists.
 */
export interface AiReveal {
  score: {
    total: number;
    summary: string;
    scoring_confidence: string | null;
    needs_review: boolean;
    model: string;
    created_at: string;
    // The rubric the score was produced under (integer version + call_type, NOT
    // a rubric_version_id) + the scorer's structured outcome (§7.5).
    call_type?: CallType | null;
    rubric_version?: number | null;
    call_outcome?: CallOutcome | null;
  } | null;
  items: Array<{
    criterion_key: string;
    label: string;
    weight: number;
    points: number;
    confidence: string | null;
    rationale: string;
    coaching_tip: string;
    evidence_ms: number[];
  }>;
  annotations: Array<{
    at_ms: number;
    type: string;
    title: string;
    body: string;
    criterion_key: string | null;
  }>;
  /**
   * The ai_audit_log id of the production scoring run for this call (latest
   * run_type='production', feature_key='call_scoring'). Ties the AI-reveal
   * feedback widget (POST /api/feedback) to the audit row so a 👍/👎 can be
   * recorded. null when no production scoring run is resolvable (e.g. not yet
   * scored) — the widget is then disabled.
   */
  audit_id: string | null;
}

// ── AI admin config (GET/PUT /api/admin/ai-config) ──────────────────────────
/** Shape returned by the admin AI-settings endpoints (model + system prompt). */
export interface AiConfig {
  model: string;
  model_options: string[];
  system_prompt: string;
  system_prompt_is_custom: boolean;
}

// ── Admin audit viewer (GET /api/admin/audit) ──────────────────────────────
/**
 * One row in the admin AI audit log (GET /api/admin/audit → `{ rows }`).
 *
 * Mirrors the ai_audit_log columns the server returns, tenant-scoped and newest
 * first, with an aggregated up/down feedback summary joined from ai_feedback.
 * The prompts + response are included so an admin can expand a row to inspect
 * exactly what was sent/returned (admin-only; no API keys live in this table).
 * Numeric cost/token/duration fields are nullable (older rows may lack them).
 */
export interface AuditEntry {
  id: string;
  feature_key: string;
  run_type: string;
  variant_label: string | null;
  model: string | null;
  provider: string | null;
  rubric_version: number | null;
  tokens_in: number | null;
  tokens_out: number | null;
  cost_cents: number | null;
  duration_ms: number | null;
  status: string;
  created_at: string; // ISO timestamptz
  // Aggregated feedback for this audit row.
  up_count: number;
  down_count: number;
  // Full prompt + response so the UI can expand a row to inspect the run.
  system_prompt: string | null;
  user_prompt: string | null;
  response: string | null;
}

/** Query params for GET /api/admin/audit (all optional; limit ≤200). */
export interface AuditFilters {
  call_id?: string;
  feature_key?: string;
  run_type?: string;
  from?: string; // ISO
  to?: string; // ISO
  limit?: number;
}

// ── STT engine A/B compare (admin → /api/admin/stt/*) ───────────────────────
export type SttRatingStatus = "fail" | "pass" | "best";

export interface SttEngine {
  provider: string;
  label: string;
  models: string[];
  default_model: string;
  key_set: boolean;
  key_source: "stored" | "env" | "none";
}

export interface SttCallOption {
  id: string;
  started_at: string;
  direction: string;
  from_number: string | null;
  to_number: string | null;
  from_name: string | null;
  to_name: string | null;
  detected_rep_name: string | null;
  duration_sec: number;
}

export interface SttSegment {
  speakerRaw?: string;
  speaker?: string;
  startMs: number;
  endMs: number;
  text: string;
}

export interface SttEvalRun {
  id: string;
  slot: string;
  status: "ok" | "error";
  error: string | null;
  segments: SttSegment[];
  speaker_count: number | null;
  diarization_confidence: number | null;
  rating: { score: number; status: SttRatingStatus } | null;
}

export interface SttEvalBatch {
  call_id: string;
  audio_url: string | null;
  runs: SttEvalRun[];
}

export interface SttReveal {
  slot: string;
  provider: string;
  model: string;
  latency_ms: number | null;
  cost_cents: number | null;
  speaker_count: number | null;
  status: string;
}

export interface SttLeaderRow {
  provider: string;
  model: string;
  matchups: number;
  wins: number;
  win_rate: number | null;
  championships: number;
  avg_latency_ms: number | null;
  avg_cost_cents: number | null;
}

export interface ProviderSecretStatus {
  provider: string;
  is_set: boolean;
  source: "stored" | "env" | "none";
  last4: string | null;
  updated_at: string | null;
}

// ── AI Settings hub (admin) — mirror AI_SETTINGS_BUILD_CONTRACT §B JSON ──────
//
// These power the tabbed admin "AI Settings hub" (web/src/screens/admin/
// AiSettings.tsx). Each shape matches a server admin endpoint exactly. The four
// real call types editable here are REAL_CALL_TYPES minus `unknown`.

/**
 * GET/PUT /api/admin/ai-config — the after-call grader.
 *
 * Extends the legacy AiConfig with `type_guidance`: a per-call-type guidance
 * block where `text` is the effective value (admin override or the code
 * TYPE_GUIDANCE default) and `is_custom` flags an override. PUT sends
 * `type_guidance` as `{ [callType]: string }` (per type; '' clears that type
 * back to the code default).
 */
export interface GraderConfig {
  model: string;
  model_options: string[];
  system_prompt: string;
  system_prompt_is_custom: boolean;
  type_guidance: { [callType: string]: { text: string; is_custom: boolean } };
}

/**
 * One row in the per-type rubric editor (GET/PUT /api/admin/rubric). `weight`
 * is an integer ≥0; all rows for a type must sum to EXACTLY 100. `full_credit`
 * /`partial_credit`/`zero_credit` are the §4 scoring anchors.
 */
export interface RubricCriterionEdit {
  key: string;
  label: string;
  weight: number;
  sort: number;
  full_credit: string;
  partial_credit: string;
  zero_credit: string;
}

/** GET /api/admin/rubric?call_type=X — the CURRENT rubric version for a type. */
export interface RubricEditor {
  call_type: CallType;
  version: number;
  criteria: RubricCriterionEdit[];
}

/** GET/PUT /api/admin/classifier-config — the call-type classifier. */
export interface ClassifierConfig {
  model: string;
  model_options: string[];
  system_prompt: string;
  system_prompt_is_custom: boolean;
  staff_first_names: string[];
}

/** One approved unique-selling-point used by the value-builders grader. */
export interface ApprovedUsp {
  family: string;
  label: string;
  guidance: string;
}

/** GET/PUT /api/admin/value-builders — USPs + review thresholds + exclusions. */
export interface ValueBuilders {
  approved_usps: ApprovedUsp[];
  manager_review_below: number;
  manager_review_critical_misses: string[];
  scoring_excludes_call_types: string[];
  call_type_options: string[];
  critical_miss_options: string[];
}

/**
 * A live-coaching reference card (GET/PUT /api/admin/live-coaching-config).
 *
 * NOTE: distinct from the runtime `ReferenceCard` (the WebSocket card carrying
 * `category`, consumed by the /live panel). This admin-config card uses `type`
 * and is the editable SOURCE the voice service resolves at call-start.
 */
export interface LiveReferenceCard {
  id: string;
  triggers: string[];
  type: "script" | "pricing" | "objection";
  title: string;
  body: string;
}

/**
 * GET/PUT /api/admin/call-log-privacy — the call-log blocklist.
 *
 * `blocked_numbers` is the raw, as-entered list of phone numbers whose calls are
 * HIDDEN from the after-call call-log reviewer for staff + managers (admins still
 * see them; the live coach is unaffected). Display-friendly strings; the server
 * normalizes only at match time.
 */
export interface CallLogPrivacy {
  blocked_numbers: string[];
}

/** GET/PUT /api/admin/live-coaching-config — the live nudge engine config. */
export interface LiveCoachingConfig {
  enabled: boolean;
  model: string;
  model_options: string[];
  system_prompt: string;
  system_prompt_is_custom: boolean;
  max_per_call: number;
  min_gap_ms: number;
  ttl_ms: number;
  eval_floor_ms: number;
  reference_cards: LiveReferenceCard[];
  /** GUI v2 live copilot toggle (ships dark) + its own prompt override. */
  gui_v2_enabled: boolean;
  system_prompt_v2: string;
  system_prompt_v2_is_custom: boolean;
  /** Show the live transcript panel under the checklist. */
  show_transcript: boolean;
}

/** A watchable live line (GET /api/live/lines) — coached extension + rep name. */
export interface LiveLine {
  extension: string;
  rep_name: string | null;
}
export interface LiveLinesResponse {
  lines: LiveLine[];
  /** Admin toggle (tenant_config live_show_transcript): show the live transcript panel. */
  show_transcript: boolean;
}

/* ── General settings (Operations = tier 1, Advanced = tier 2) ───────────────
 * GET/PUT /api/admin/settings. The SERVER owns the schema (lib/settingsSchema.ts)
 * and ships it with current values, so these tabs render generically — a new
 * editable key is one server descriptor, no UI code. */
export type SettingKind =
  | "boolean"
  | "int"
  | "number"
  | "string"
  | "text"
  | "enum"
  | "string_list"
  | "json";

export interface SettingField {
  key: string;
  tier: 1 | 2;
  group: string;
  label: string;
  help?: string;
  kind: SettingKind;
  min?: number;
  max?: number;
  options?: string[];
  placeholder?: string;
  /** Current resolved value (tenant override → default). */
  value: unknown;
}

export interface SettingsResponse {
  fields: SettingField[];
}

// ── Manager ─────────────────────────────────────────────────────────────────
/**
 * The single manager flag for a call, as returned in the coaching payload
 * (GET /api/calls/:id/coaching → `flag`). At most one flag exists per call.
 * `assigned_to_name` is the resolved profile name for `assigned_to`.
 */
export interface ManagerFlag {
  type: "exemplary" | "needs_work";
  note: string | null;
  shared: boolean;
  assigned_to: string | null;
  assigned_to_name: string | null;
  due_date: string | null;
  status: FlagLifecycleStatus;
  /** When the assigned rep acknowledged the coaching (null until then). */
  acknowledged_at: string | null;
}

/**
 * The full manager_flags row returned by PATCH /api/calls/:id/flag (the
 * coaching-lifecycle transition endpoint, CP3).
 */
export interface FlagLifecycleRow {
  id: string;
  call_id: string;
  type: "exemplary" | "needs_work" | "auto_review";
  created_by: string | null;
  assigned_to: string | null;
  note: string | null;
  status: FlagLifecycleStatus;
  due_date: string | null;
  shared: boolean;
  acknowledged_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * The manager's final score of record for a call (GET /api/calls/:id/coaching →
 * `override`). One override per call; `note` is required (shown to the rep).
 * Each item snapshots the rubric criterion label + weight at override time.
 */
export interface ManagerOverride {
  total: number;
  note: string;
  manager_name: string | null;
  created_at: string;
  items: Array<{
    criterion_key: string;
    label: string;
    weight: number;
    points: number;
    note: string | null;
  }>;
}

/** GET /api/calls/:id/coaching — flag + override for the call (manager view). */
export interface Coaching {
  flag: ManagerFlag | null;
  override: ManagerOverride | null;
}

/**
 * GET /api/manager/queue — one open coaching item (a call with an open flag),
 * carrying the attributed rep (who actually spoke), extension, AI total, and
 * the flag fields. Newest first.
 */
export interface ManagerQueueItem {
  /** The manager_flags row id (transition target). */
  id: string;
  call_id: string;
  type: string;
  note: string | null;
  shared: boolean;
  assigned_to_name: string | null;
  due_date: string | null;
  status: FlagLifecycleStatus;
  acknowledged_at: string | null;
  /** The attributed rep's profile id (null = unattributed) — agent-profile link. */
  rep_id: string | null;
  rep_name: string | null;
  extension_number: string | null;
  total: number | null;
  started_at: string | null;
}

/** GET /api/manager/staff — active staff/manager for the assign dropdown. */
export interface StaffMember {
  id: string;
  name: string;
  extension_number: string | null;
}

/**
 * GET /api/manager/dashboard — per-rep score trends (manager/admin only).
 *
 * `recent` is a short series of recent AI totals (oldest→newest) for sparklines.
 * `avg_total` values are computed over scored calls only.
 */
export interface ManagerDashboard {
  reps: Array<{
    user_id: string | null;
    name: string;
    extension_number: string | null;
    scored_count: number;
    avg_total: number;
    last_scored_at: string | null;
    recent: number[];
  }>;
  overall: {
    scored_count: number;
    avg_total: number;
  };
  recent_calls: Array<{
    call_id: string;
    rep_name: string;
    extension_number: string | null;
    total: number;
    started_at: string;
    direction: string;
  }>;
}

// ── Calibration (GET /api/manager/calibration) — CP1 ─────────────────────────
/**
 * One rep's self-vs-AI calibration (GET /api/manager/calibration → `reps[]`).
 *
 * Computed over the tenant's calls that have BOTH a submitted self-score AND an
 * AI score (manager/admin only; respects the call-log blocklist for non-admins
 * and excludes calls excluded_from_stats). `avg_gap` = avg_self − avg_ai, so a
 * positive gap means the rep grades themselves HIGHER than the AI (over-scorer);
 * a negative gap means they under-score themselves. `avg_override` is the avg
 * manager final score where one exists, else null. The over/under flags are set
 * by the server against its calibration threshold.
 */
export interface CalibrationRep {
  rep_id: string | null;
  rep_name: string;
  n_calls: number;
  avg_self: number;
  avg_ai: number;
  avg_gap: number;
  avg_override: number | null;
  over_scorer: boolean;
  under_scorer: boolean;
  /** True only for the synthetic tenant-total row (distinct from the rep_id=null
   *  "Unassigned" bucket, which is a real per-rep group). */
  is_overall: boolean;
}

/**
 * GET /api/manager/calibration — per-rep calibration + the resolved window.
 *
 * The contract's shape is `{ reps: CalibrationRep[], window }`; the "overall row"
 * is carried inside `reps` as a synthetic rep with `rep_id === null` (the screen
 * pulls it out and renders it distinctly). `window` echoes the resolved range.
 */
export interface Calibration {
  reps: CalibrationRep[];
  window: { from: string | null; to: string | null };
}

/** Query params for GET /api/manager/calibration (same window semantics as stats). */
export interface CalibrationFilters {
  from?: string; // ISO
  to?: string; // ISO
}

// ── Agent profile (GET /api/manager/agents/:id) — CP3 ───────────────────────
/** One of the rep's in-flight coaching items (assigned to them OR on their call). */
export interface AgentProfileFlag {
  id: string;
  call_id: string;
  type: "exemplary" | "needs_work" | "auto_review";
  note: string | null;
  shared: boolean;
  assigned_to: string | null;
  due_date: string | null;
  status: FlagLifecycleStatus;
  acknowledged_at: string | null;
  created_at: string;
  /** The flagged call's started_at (null if unknown). */
  started_at: string | null;
}

/**
 * Per-rep development rollup (manager/admin only). Counted-call semantics match
 * the manager dashboard exactly (sales-only headline; per_type keeps service in
 * its own bucket; blocklist respected server-side for non-admins).
 */
export interface AgentProfile {
  rep: {
    id: string;
    name: string | null;
    role: AppRole;
    active: boolean;
    extension_number: string | null;
  };
  window: { from: string | null; to: string | null };
  summary: {
    scored_count: number;
    avg_total: number | null;
    live_scored_count: number;
  };
  /** Counted SALES scored calls, oldest → newest (capped server-side). */
  trend: Array<{
    call_id: string;
    total: number;
    started_at: string | null;
    scored_at: string;
    call_type: string | null;
    transcript_source: TranscriptSource | null;
  }>;
  /** Per-call-type averages over the counted set (service in its own bucket). */
  per_type: Record<string, { avg: number | null; count: number }>;
  /** This rep's calibration row (null when no submitted-self + AI pairs). */
  calibration: CalibrationRep | null;
  open_flags: AgentProfileFlag[];
}

// ── Saved views (GET/POST/DELETE /api/views) — CP3, per-user ────────────────
/**
 * A named bundle of Dashboard filters the user keeps for one-click recall.
 * Strictly personal (owner-only). `filters` is an opaque scalar bag — the
 * Dashboard stores its FilterState entries and re-validates them on apply;
 * replaying ALWAYS goes back through GET /api/calls, which re-applies every
 * gate (staff scoping, reveal gate, privacy blocklist) — a view grants nothing.
 */
export interface SavedView {
  id: string;
  name: string;
  filters: Record<string, unknown>;
  created_at: string;
}

// ── Recording ───────────────────────────────────────────────────────────────
/**
 * GET /api/calls/:id/recording-url — short-lived signed URL.
 *
 * `url` is the audio of record (the RingCentral recording, OR the live
 * dual-channel WAV when the call was scored from live + stored as stereo).
 * The Phase-4 cutover fields are OPTIONAL (the server stream is parallel; older
 * responses omit them) and best-effort:
 *   - `audio_source` mirrors calls.audio_source so the player can label the source;
 *   - `offset_ms` is the recording-t0 → live-t0 offset to align a LIVE transcript
 *     against the RingCentral recording (absent / 0 when the WAV shares the clock);
 *   - `dual_channel` flags a true stereo (rep-left / customer-right) WAV.
 */
export interface RecordingUrl {
  url: string;
  expires_at: string; // ISO
  audio_source?: AudioSource;
  /** recording-t0 → live-t0 offset (ms) applied when syncing a live transcript. */
  offset_ms?: number | null;
  /** True when `url` is the live stereo WAV (rep L / customer R). */
  dual_channel?: boolean;
}

// ── Feedback ────────────────────────────────────────────────────────────────
/** Body for POST /api/feedback. */
export interface FeedbackSubmission {
  audit_id: string;
  rating: FeedbackRating;
  note?: string;
  criterion_key?: string;
}

// ── List filters (GET /api/calls query params) ──────────────────────────────
// Matches the server listQuery (routes/calls.ts) 1:1. The gated filters
// (selfScore / aiReview='scored' / score band / notes) are applied server-side
// against the SAME gated values the rows carry, so a staff caller can never use
// a filter to probe an unrevealed AI score. /api/calls/stats accepts the same
// shape minus `scoreBand`/`notes`/`limit`/`offset` (unknown keys are stripped).
export interface CallListFilters {
  extension?: string;
  direction?: CallDirection;
  from?: string; // ISO date
  to?: string; // ISO date
  flag?: FlagType;
  status?: PipelineStatus;
  scoreMin?: number;
  scoreMax?: number;
  /** Free-text search over name/number/extension (case-insensitive substring). */
  search?: string;
  selfScore?: "submitted" | "pending";
  aiReview?: "reviewed" | "scored" | "none";
  /** "Not scored": only calls with NO visible AI score (gated view). */
  scoreBand?: "unscored";
  /** Manager-notes presence (reads the GATED manager_notes_count). */
  notes?: "has" | "none";
  // Real server pagination (metadata rides on X-Total-Count / X-Has-More headers).
  limit?: number;
  offset?: number;
}

/** One page of GET /api/calls plus the header-borne pagination metadata. */
export interface CallListPage {
  rows: CallRow[];
  /** Post-filter total within the server's scan window (a floor when truncated). */
  total: number;
  offset: number;
  hasMore: boolean;
  /** True when the server hit its scan cap — `total` is then a lower bound. */
  scanTruncated: boolean;
}

// ── Health ──────────────────────────────────────────────────────────────────
export interface HealthStatus {
  ok: boolean;
  last_poll_at: string | null;
  queue_depth: number;
}

// ── API error envelope ──────────────────────────────────────────────────────
export interface ApiError {
  error: string;
  message?: string;
  details?: unknown;
}

// ── Live coaching (M3) ───────────────────────────────────────────────────────
// These MIRROR voice/src/coach/types.ts exactly. The voice service is the source
// of truth for the WebSocket contract; this is the client-side copy the /live
// side panel decodes against. Keep the keys, weights, and CoachingEvent variants
// in lock-step with that file — if the server contract changes, change both.

/** The 4 rubric criteria — IDENTICAL keys to the after-call rubric. */
export type CriterionKey =
  | "captured_customer_vehicle_reasons"
  | "built_value_usp"
  | "offered_premium"
  | "confirm_booking";

// Named LiveRubricCriterion (not RubricCriterion) to avoid colliding with the
// after-call RubricCriterion above — that one carries `sort` and a string key;
// this one is the live-coaching shape with a typed CriterionKey. Same fields as
// voice/src/coach/types.ts RubricCriterion otherwise.
export interface LiveRubricCriterion {
  key: CriterionKey;
  label: string;
  weight: number;
}

/** Canonical rubric (mirrors voice RUBRIC; weights total 100). */
export const RUBRIC: LiveRubricCriterion[] = [
  { key: "captured_customer_vehicle_reasons", label: "Captured customer, vehicle & reasons", weight: 20 },
  { key: "built_value_usp", label: "Built value in Tint King / USP", weight: 20 },
  { key: "offered_premium", label: "Offered premium products", weight: 20 },
  { key: "confirm_booking", label: "Confirmed the booking", weight: 40 },
];

/** One transcript line from streaming STT, speaker-attributed. */
export interface TranscriptLine {
  speaker: "rep" | "customer" | "unknown";
  text: string;
  /** ms from call start. */
  startMs: number;
  /** false = interim/partial, true = finalized. */
  final: boolean;
}

/** Live state of one rubric criterion during the call. */
export interface CriterionState extends LiveRubricCriterion {
  covered: boolean;
  /** ms from call start when it first flipped covered. */
  coveredAtMs: number | null;
}

export type RubricState = CriterionState[];

/** A single timely nudge shown to the rep. */
export interface Nudge {
  id: string;
  text: string;
  /** The criterion it relates to, if any. */
  criterionKey: CriterionKey | null;
  severity: "info" | "prompt" | "urgent";
  /** ms from call start when emitted. */
  atMs: number;
  /** ms TTL after which the panel auto-clears this nudge. */
  ttlMs: number;
}

/** A static reference card (scripts / pricing / objection handling). */
export interface ReferenceCard {
  id: string;
  category: "script" | "pricing" | "objection";
  title: string;
  body: string;
  /** Keywords that, when the CUSTOMER says them, surface this card. */
  triggers: string[];
}

/** One party in a raw RingCentral telephony event (debug visualizer). */
export interface RcEventParty {
  ext: string | null;
  status: string | null;
  number: string | null;
}

/* ────────────────────────────────────────────────────────────────────────────
 * GUI v2 (live copilot) contracts — MIRRORS voice/src/coach/types.ts; keep in
 * lock-step. Grounding: 03_stage-by-stage-advisor-guide.md (12 master stages),
 * 24_live-ai-coaching-prompts.md, GUI v2 functional requirements §A.1.
 * ──────────────────────────────────────────────────────────────────────────── */

/** The 12 master call stages (03_stage-by-stage-advisor-guide.md). */
export type StageId = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

/** The six display zones the strip renders (GUI v2 req §A.1). */
export type ZoneKey =
  | "open"
  | "discover"
  | "build_value"
  | "present_price"
  | "objections"
  | "close_book";

/** 12 → 6 grouping: open={1,2} discover={3,4,5,6} build_value={7,8}
 *  present_price={9} objections={10} close_book={11,12}. */
export const STAGE_ZONE: Record<StageId, ZoneKey> = {
  1: "open",
  2: "open",
  3: "discover",
  4: "discover",
  5: "discover",
  6: "discover",
  7: "build_value",
  8: "build_value",
  9: "present_price",
  10: "objections",
  11: "close_book",
  12: "close_book",
};

export type ServiceInterest = "auto_tint" | "ppf" | "ceramic_coating" | "bundle" | "unsure";

/** Reason-for-calling chips (GUI v2 req 1.7 + 25_breadcrumb-data-model.md). */
export type ReasonKey =
  | "heat"
  | "glare"
  | "privacy"
  | "uv_protection"
  | "kids"
  | "appearance"
  | "interior_protection"
  | "new_vehicle"
  | "had_tint_before"
  | "comparing_quotes"
  | "paint_protection"
  | "resale_value"
  | "easier_maintenance"
  | "fuel_costs";

export type BodyStyle =
  | "sedan"
  | "coupe"
  | "hatchback"
  | "convertible"
  | "wagon"
  | "truck"
  | "suv"
  | "crossover"
  | "van"
  | "minivan";

/** Tint pricing splits Car/Truck vs Van/SUV (02_source-of-truth-checklist.md §2). */
export type PricingClass = "car_truck" | "van_suv";

/** Where a call-card value came from. Manual always wins (req 1.2/1.5). */
export type FieldSource = "caller_id" | "transcript" | "manual";

/** One auto-populated, tap-correctable card field (unconfirmed = ghost). */
export interface CardField<T> {
  value: T | null;
  confirmed: boolean;
  source: FieldSource | null;
}

export interface VehicleState {
  year: CardField<number>;
  make: CardField<string>;
  model: CardField<string>;
  bodyStyle: CardField<BodyStyle>;
  /** Deterministic from bodyStyle — never model-generated (req 1.6/5.2). */
  pricingClass: PricingClass | null;
  /** True while the displayed class rests on an assumption (req 5.6). */
  classAssumed: boolean;
  /** Body style/door count unresolved — card flags it + a nudge asks (req 1.6). */
  ambiguous: boolean;
  /** Existing tint mentioned → removal line auto-surfaces (req 5.5/A.7). */
  existingTint: boolean | null;
}

export interface ReasonChip {
  key: ReasonKey;
  /** false = amber chip with trailing "?" → confirmation nudge (req A.8). */
  confirmed: boolean;
}

/** The pinned call card (GUI v2 feature 1). Sent as a full snapshot on change. */
export interface CallCardState {
  firstName: CardField<string>;
  lastName: CardField<string>;
  phone: CardField<string>;
  serviceInterest: CardField<ServiceInterest>;
  reasons: ReasonChip[];
  vehicles: VehicleState[];
  activeVehicle: number;
}

/** Detectable advisor behaviors — the shared detection set both scoreboards read (G.5). */
export type BehaviorKey =
  | "greeted_named_self"
  | "name_captured"
  | "phone_captured"
  | "vehicle_captured"
  | "existing_tint_asked"
  | "usage_question_asked"
  | "product_path_clarified"
  | "why_now_asked"
  | "implication_question_asked"
  | "motivation_captured"
  | "gap_confirmed"
  | "education_moment"
  | "recommendation_made"
  | "premium_offered"
  | "conversion_play_offered"
  | "value_tied_to_reason"
  | "warranty_named"
  | "exclusivity_claimed"
  | "price_stated"
  | "deposit_math_mentioned"
  | "prepay_disclosed_over_1000"
  | "financing_qualified_phrase"
  | "objection_acknowledged"
  | "objection_clarified"
  | "next_step_asked_after_objection"
  | "booking_asked"
  | "two_times_offered"
  | "deposit_asked"
  | "text_to_pay_sent"
  | "twelve_hour_window_stated"
  | "cancellation_terms_stated"
  | "showroom_invited"
  | "booking_confirmed"
  | "readback_done"
  | "estimate_send_committed"
  | "followup_set";

/** The transcript snippet that triggered a detection (evidence affordance, req 2.2). */
export interface Evidence {
  text: string;
  speaker: TranscriptLine["speaker"];
  atMs: number;
}

export interface ChecklistSubItem {
  key: "name" | "vehicle" | "reason";
  label: string;
  covered: boolean;
  evidence: Evidence | null;
}

/** GUI v2 checklist item — CriterionState + evidence + manual override + sub-items. */
export interface ChecklistItemState extends CriterionState {
  evidence: Evidence | null;
  manual: boolean;
  subItems: ChecklistSubItem[] | null;
}

export type ChecklistState = ChecklistItemState[];

/** Nudge taxonomy (GUI v2 req 3.1). */
export type NudgeKind =
  | "discovery"
  | "talk_track"
  | "objection"
  | "process"
  | "upsell"
  | "confirm_reason"
  | "compliance";

/** GUI v2 nudge extras (optional so v1 nudges stay valid). */
export interface NudgeMeta {
  kind?: NudgeKind;
  promptId?: string | null;
  /** Sales-guide citation shown by WHY (req A.6). */
  sourceRef?: string | null;
  evidence?: Evidence | null;
}

/** Compliance alert ids — 24_live-ai-coaching-prompts.md §D (copy in live/content). */
export type ComplianceAlertId =
  | "fuel_numbers"
  | "legal_improvised"
  | "coating_as_chip_protection"
  | "financing_guarantee"
  | "flat_heat_number"
  | "off_sheet_spec"
  | "scratch_proof"
  | "ppf_install_by_phone"
  | "clock_time_promise";

/** Auto-fail conditions (18_call-scorecard.md §5). 3+4 fire live; 1+2 at wrap-up. */
export type AutoFailId =
  | "no_crm_minimums"
  | "no_close_attempt"
  | "prohibited_claim"
  | "improvised_legal";

/** WebSocket messages pushed from the voice hub to a rep's side panel. */
export type CoachingEvent =
  | { type: "hello"; sessionId: string; callId: string | null; rubric: RubricState }
  | { type: "transcript"; line: TranscriptLine }
  | { type: "rubric"; rubric: RubricState }
  | { type: "nudge"; nudge: Nudge & NudgeMeta }
  | { type: "reference"; cardIds: string[] }
  | { type: "call_started"; startedAt: string; counterpart: string | null }
  | { type: "call_ended"; endedAt: string }
  // ── GUI v2 events ──
  | { type: "call_ringing"; counterpart: string | null }
  /** Which coached extensions currently have a live call (line-chip status + auto-follow). */
  | { type: "lines_status"; live: string[] }
  | { type: "call_card"; card: CallCardState }
  | { type: "stage"; stageId: StageId }
  | { type: "checklist"; checklist: ChecklistState }
  | { type: "behavior"; key: BehaviorKey; evidence: Evidence | null }
  | { type: "objection"; objectionId: string; evidence: Evidence | null }
  | { type: "buying_signal"; evidence: Evidence | null }
  | { type: "compliance_alert"; alertId: ComplianceAlertId; evidence: Evidence | null }
  // ── debug visualizer: raw RingCentral feed, independent of supervise/SIP ──
  | { type: "rc_status"; transport: "websocket" | "webhook"; state: string; detail: string | null }
  | {
      type: "rc_event";
      at: string;
      telephonySessionId: string | null;
      inScope: boolean;
      parties: RcEventParty[];
    };

/** Messages the panel sends UPSTREAM on the /coach socket (one-tap corrections stick). */
export type PanelMessage =
  | { type: "nudge_done"; id: string; criterionKey: CriterionKey | null }
  | { type: "nudge_dismiss"; id: string; promptId: string | null }
  | { type: "checklist_set"; key: CriterionKey; covered: boolean }
  | {
      type: "card_set";
      patch: {
        firstName?: string | null;
        lastName?: string | null;
        serviceInterest?: ServiceInterest | null;
        vehicle?: {
          index: number;
          year?: number | null;
          make?: string | null;
          model?: string | null;
          bodyStyle?: BodyStyle | null;
        };
        activeVehicle?: number;
      };
    }
  | { type: "reason_set"; key: ReasonKey; state: "confirmed" | "removed" }
  | { type: "disposition_set"; key: string };
